import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import SuperAdminService, {
  type SuperAdminReminder,
  type SuperAdminReminderKind,
} from '../services/SuperAdminService';

/**
 * SuperAdminRemindersContext — süperadmin hatırlatmalarının tek veri kaynağı.
 *
 * Süreye bağlı tüm takip kalemlerini tek listede toplar: yazarkasa GMÜ süreleri
 * ve şirket sözleşmeleri. Ön muhasebedeki DueRemindersContext ile aynı deseni
 * izler ve iki tüketiciye birlikte servis eder:
 *  - SuperAdminRemindersNotifier → sağ altta çıkan uyarı bandı (günlük, kapatılabilir)
 *  - SuperAdminRemindersBell     → süperadmin başlığındaki bildirim zili ve sayacı
 *
 * Bu bir SİSTEM bildirimidir: içerik canlı veriden türetilir, okundu/okunmadı
 * tutulmaz. Kalem yenilendiğinde (bitiş tarihi ileri alındığında) listeden
 * kendiliğinden düşer, rozet sayısı azalır.
 *
 * Kapsam backend'de belirlenir ve panelin sekmeleriyle birebir örtüşür: bayi
 * kendi şirketlerinin sözleşmelerini ve KENDİ verdiği yazarkasaları görür, süper
 * admin hepsini görür. Böylece her hatırlatma, tıklandığında gerçekten açılabilen
 * bir sekmeye derin bağlantı verir.
 */

/** Kaç gün kala hatırlatma başlasın. */
export const REMINDER_WARNING_DAYS = 30;

/** Liste tazeleme aralığı — vade bilgisi gün bazlı, sık yenilemeye gerek yok. */
const REFRESH_INTERVAL_MS = 60 * 60 * 1000;

/** Kaynak bazlı metinler — backend ReminderKind sabitleriyle birebir eşleşir. */
export const REMINDER_KIND_TEXTS: Record<
  SuperAdminReminderKind,
  {
    /** Zil satırındaki tür rozeti. */
    name: string;
    /** Sağ alt banttaki başlık (adet ile birlikte kurulur). */
    expiredTitle: (count: number) => string;
    expiringTitle: (count: number) => string;
    goLabel: string;
  }
> = {
  FISCAL_DEVICE: {
    name: 'Yazarkasa',
    expiredTitle: (n) => `⚠️ GMÜ süresi DOLMUŞ ${n} yazarkasa`,
    expiringTitle: (n) => `⏰ ${REMINDER_WARNING_DAYS} gün içinde GMÜ süresi dolacak ${n} yazarkasa`,
    goLabel: 'Yazarkasalara Git',
  },
  CONTRACT: {
    name: 'Sözleşme',
    expiredTitle: (n) => `⚠️ Sözleşmesi BİTMİŞ ${n} şirket`,
    expiringTitle: (n) => `⏰ ${REMINDER_WARNING_DAYS} gün içinde sözleşmesi bitecek ${n} şirket`,
    goLabel: 'Sözleşmelere Git',
  },
  SUBSCRIPTION: {
    name: 'Abonelik',
    expiredTitle: (n) => `⚠️ Aboneliği bitmiş ${n} şirket — tahsilat oluşturun`,
    expiringTitle: (n) => `⏰ Aboneliği bitmek üzere ${n} şirket`,
    goLabel: 'Tahsilat Oluştur',
  },
  PRODUCT: {
    name: 'Ürün',
    expiredTitle: (n) => `⚠️ Ürün/hizmet süresi dolmuş ${n} şirket — tahsilat oluşturun`,
    expiringTitle: (n) => `⏰ Ürün/hizmet süresi yaklaşan ${n} şirket`,
    goLabel: 'Tahsilat Oluştur',
  },
  BILLING: {
    name: 'Tahsilat',
    expiredTitle: (n) => `⚠️ Yenilemesi geçmiş ${n} şirket — tahsilat oluşturun`,
    expiringTitle: (n) => `⏰ Yenilemesi yaklaşan ${n} şirket — tahsilat oluşturun`,
    goLabel: 'Tahsilat Oluştur',
  },
  DEMO: {
    name: 'Demo',
    expiredTitle: (n) => `⚠️ Demosu dolmuş ${n} şirket — tahsilat oluşturun (lisansa geçiş)`,
    expiringTitle: (n) => `⏰ Demosu bitmek üzere ${n} şirket — tahsilat oluşturun (lisansa geçiş)`,
    goLabel: 'Tahsilat Oluştur',
  },
};

interface SuperAdminRemindersContextValue {
  /** Süresi dolmuş + 30 gün içinde dolacak kalemler (en kritik önce). */
  reminders: SuperAdminReminder[];
  /** Süresi geçmiş kalem sayısı. */
  expiredCount: number;
  /** Süresi yaklaşan (bugün dahil) kalem sayısı. */
  expiringCount: number;
  /** Zil rozetinde gösterilen toplam. */
  totalCount: number;
  /** Ertelenen / kapatılan uyarı + takibi bırakılan şirket sayısı. */
  mutedCount: number;
  loading: boolean;
  refresh: () => Promise<void>;
}

const SuperAdminRemindersContext = createContext<SuperAdminRemindersContextValue>({
  reminders: [],
  expiredCount: 0,
  expiringCount: 0,
  totalCount: 0,
  mutedCount: 0,
  loading: false,
  refresh: async () => {},
});

export const useSuperAdminReminders = () => useContext(SuperAdminRemindersContext);

export const SuperAdminRemindersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [reminders, setReminders] = useState<SuperAdminReminder[]>([]);
  const [mutedCount, setMutedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const fetchingRef = useRef(false);

  const refresh = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    try {
      const { rows, mutedCount: muted } = await SuperAdminService.getInstance().getSuperAdminRemindersWithMeta(REMINDER_WARNING_DAYS);
      setReminders([...rows].sort((a, b) => a.daysRemaining - b.daysRemaining));
      setMutedCount(muted);
    } catch (err) {
      // Hatırlatma sistemi paneli asla bloklamaz.
      console.warn('[SuperAdminReminders] Süre kontrolü yapılamadı:', err);
    } finally {
      fetchingRef.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [refresh]);

  const value = useMemo<SuperAdminRemindersContextValue>(() => {
    const expiredCount = reminders.filter((r) => r.daysRemaining < 0).length;
    return {
      reminders,
      expiredCount,
      expiringCount: reminders.length - expiredCount,
      totalCount: reminders.length,
      mutedCount,
      loading,
      refresh,
    };
  }, [reminders, mutedCount, loading, refresh]);

  return (
    <SuperAdminRemindersContext.Provider value={value}>
      {children}
    </SuperAdminRemindersContext.Provider>
  );
};

/** "5 gün geçti" / "Bugün doluyor" / "12 gün kaldı". */
export const reminderTiming = (daysRemaining: number): string => {
  if (daysRemaining < 0) return `${Math.abs(daysRemaining)} gün geçti`;
  if (daysRemaining === 0) return 'Bugün doluyor';
  return `${daysRemaining} gün kaldı`;
};
